import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { motion, AnimatePresence } from 'framer-motion';

interface QuizStep {
  question: string;
  options: {
    value: string;
    label: string;
    description?: string;
  }[];
}

const quizSteps: QuizStep[] = [
  {
    question: "Are you ready to step outside your usual travel comfort zone and explore places you never thought you'd love?",
    options: [
      { 
        value: "unknown", 
        label: "Yes—I crave the unknown",
        description: "Discover hidden gems and unexpected delights"
      },
      { 
        value: "familiar", 
        label: "No—I stick with what I know works for me",
        description: "Experience tried-and-true destinations with established attractions"
      }
    ]
  },
  {
    question: "Do you prefer a day that surprises you at every turn, or one where every detail is planned in advance?",
    options: [
      { 
        value: "spontaneous", 
        label: "I love surprises—I want every moment to feel spontaneous",
        description: "Embrace the unexpected and let each day unfold naturally"
      },
      { 
        value: "planned", 
        label: "I prefer a well-laid plan and a predictable itinerary",
        description: "Follow structured itineraries with clear attractions"
      }
    ]
  },
  {
    question: "Which scene sparks your interest most for your next trip?",
    options: [
      { 
        value: "culture", 
        label: "A deep dive into local history, art, and traditions",
        description: "Immerse yourself in rich cultural heritage"
      },
      { 
        value: "nature", 
        label: "Immersion in breathtaking landscapes and natural wonders",
        description: "Experience stunning natural beauty"
      },
      { 
        value: "blend", 
        label: "I'd love a blend of cultural vibes and natural beauty",
        description: "Get the best of both worlds"
      }
    ]
  },
  {
    question: "Do you want to uncover unusual local spots and offbeat experiences that you never expected to enjoy?",
    options: [
      { 
        value: "quirky", 
        label: "Absolutely—I'm all for the quirky and unexpected",
        description: "Find unique and surprising experiences"
      },
      { 
        value: "classic", 
        label: "Not really—I prefer classic tourist attractions",
        description: "Visit well-known and popular attractions"
      }
    ]
  },
  {
    question: "How flexible is your budget when it comes to chasing a hidden gem?",
    options: [
      { 
        value: "budget", 
        label: "I'm on a tight budget—I need affordable surprises",
        description: "Discover budget-friendly destinations"
      },
      { 
        value: "luxury", 
        label: "I'm willing to invest for unforgettable experiences",
        description: "Experience premium and exclusive offerings"
      },
      { 
        value: "moderate", 
        label: "I prefer a balance between cost and adventure",
        description: "Find destinations with good value for money"
      }
    ]
  },
  {
    question: "Do you enjoy the travel process itself—even if it means extra stops or less direct routes—to uncover hidden treasures?",
    options: [
      { 
        value: "scenic", 
        label: "Yes, I'm willing to take the scenic, winding road",
        description: "Embrace journey-focused travel experiences"
      },
      { 
        value: "direct", 
        label: "No, I need the most direct and hassle-free route possible",
        description: "Focus on easily accessible destinations"
      }
    ]
  },
  {
    question: "On vacation, do you need a constant digital connection, or do you long for a complete escape?",
    options: [
      { 
        value: "connected", 
        label: "I need to stay connected—good internet and modern amenities are a must",
        description: "Stay in touch with modern connectivity"
      },
      { 
        value: "disconnect", 
        label: "I'm ready to disconnect and immerse myself in the moment",
        description: "Experience genuine local life without digital distractions"
      }
    ]
  },
  {
    question: "Sometimes a journey changes you in ways you never imagined. Are you ready for a destination that might just reveal a new side of you?",
    options: [
      { 
        value: "transformative", 
        label: "Yes—I'm excited to discover a new side of myself",
        description: "Embrace life-changing travel experiences"
      },
      { 
        value: "comfortable", 
        label: "I prefer destinations that match my known interests",
        description: "Stay within your comfort zone"
      },
      { 
        value: "balanced", 
        label: "I'm open to surprises with a hint of familiarity",
        description: "Find the perfect balance between new and familiar"
      }
    ]
  }
];

const processAnswers = (answers: Record<number, string>) => {
  const preferences = {
    comfortZone: answers[0], // unknown vs familiar
    planningStyle: answers[1], // spontaneous vs planned
    interestFocus: answers[2], // culture, nature, or blend
    experienceType: answers[3], // quirky vs classic
    budget: answers[4], // budget, luxury, or moderate
    journeyPreference: answers[5], // scenic vs direct
    connectivityNeeds: answers[6], // connected vs disconnect
    transformationOpenness: answers[7], // transformative, comfortable, or balanced
  };

  return preferences;
};

const Wizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = React.useState(0);
  const [answers, setAnswers] = React.useState<Record<number, string>>({});

  const progress = ((currentStep + 1) / quizSteps.length) * 100;

  const handleAnswer = (value: string) => {
    setAnswers(prev => ({ ...prev, [currentStep]: value }));
  };

  const handleNext = () => {
    if (currentStep < quizSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      const recommendations = processAnswers(answers);
      navigate('/recommendations', { state: { recommendations } });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    } else {
      // Go back to the previous page or homepage
      navigate(-1);
    }
  };

  const currentQuiz = quizSteps[currentStep];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container max-w-2xl">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6 md:p-8"
        >
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-center mb-4">
              Find Your Perfect Destination
            </h1>
            <Progress value={progress} className="h-2" />
            <p className="text-center text-gray-500 mt-2">
              Step {currentStep + 1} of {quizSteps.length}
            </p>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <h2 className="text-xl font-semibold text-center">
                {currentQuiz.question}
              </h2>

              <RadioGroup
                value={answers[currentStep]}
                onValueChange={handleAnswer}
                className="space-y-4"
              >
                {currentQuiz.options.map((option) => (
                  <div
                    key={option.value}
                    className="relative"
                  >
                    <RadioGroupItem
                      value={option.value}
                      id={option.value}
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor={option.value}
                      className="flex flex-col p-4 rounded-lg border-2 cursor-pointer transition-all hover:bg-primary/5 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5"
                    >
                      <span className="font-medium">{option.label}</span>
                      <span className="text-sm text-gray-500">{option.description}</span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              <div className="flex justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  className="flex items-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={!answers[currentStep]}
                  className="flex items-center gap-2"
                >
                  {currentStep === quizSteps.length - 1 ? "See Results" : "Next"}
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default Wizard;
